import { ArrowDownward } from "@mui/icons-material";
import { Box, Chip, Collapse, Divider, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFocusOnStaffClick } from "../../../../hooks/useFocusOnStaffClick";
import { NEUTRAL, STATUS_BADGE } from "../../../../theme/tokens";
import type { HoursSummaryEmployeeDayShift, NurseInShift } from "../../../../utils/models";
import { isAgencyNurse } from "../../../../utils/utilFunctions";
import { InHouseTooltip } from "../../../InHouseTooltip/InHouseTooltip";
import { NURSE_HIGHLIGHT_BG, NURSE_HIGHLIGHT_CLASS } from "../../WeeklyGapView/consts";

interface IProps {
	shifts: NurseInShift[];
	shiftHoursById?: Map<string, HoursSummaryEmployeeDayShift>;
	actualShiftIds?: Set<string>;
	searchText: string | null;
	onShiftDoubleClick?: (shift: NurseInShift) => void;
}

const ROW_HEIGHT = "28px";
const ROW_FONT_SIZE = "13px";
const ROW_TEXT_COLOR = "primary.dark";
const HOVER_BG = NEUTRAL[50];
const EXPANDED_MAX_HEIGHT = 100;

export const FutureLookingWeeklyOvertimeDayRecord: FC<IProps> = ({ shifts, shiftHoursById, actualShiftIds, searchText, onShiftDoubleClick }) => {
	const { t } = useTranslation();
	const [isExpanded, setIsExpanded] = useState<boolean>(false);
	const { scheduleHint, cancelHint } = useFocusOnStaffClick();

	const count = shifts.length;
	const hasItems = count > 0;

	return (
		<Box sx={{ px: 1, borderRadius: "4px" }} data-testid="high-cost-shifts-row">
			<Box
				onClick={hasItems ? () => setIsExpanded((prev) => !prev) : undefined}
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					cursor: hasItems ? "pointer" : "default",
					"&:hover": hasItems ? { bgcolor: HOVER_BG } : undefined,
					transition: "background-color 0.2s ease",
					borderRadius: "4px",
				}}
			>
				<Typography
					sx={{
						fontWeight: 400,
						fontSize: ROW_FONT_SIZE,
						color: ROW_TEXT_COLOR,
						height: ROW_HEIGHT,
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
						width: "100%",
						pt: "4px",
					}}
				>
					{`${t("costly.staff")} - ${count}`}
				</Typography>
				{hasItems && (
					<ArrowDownward
						sx={{
							color: "primary.main",
							transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
							transition: "transform 0.3s ease",
							fontSize: 16,
						}}
					/>
				)}
			</Box>
			<Collapse in={isExpanded}>
				<Box sx={{ maxHeight: EXPANDED_MAX_HEIGHT, overflowY: "auto" }} data-testid="high-cost-shifts-list">
					{shifts.map((shift) => {
						const isAgency = isAgencyNurse(shift.type);
						const isActual = actualShiftIds?.has(shift.id) ?? false;
						const shiftHours = shiftHoursById?.get(shift.id);
						let hoursDisplay = "";
						if (shiftHours) {
							if (isAgency) {
								hoursDisplay = `${shiftHours.agencyHours.toFixed(1)}h`;
							} else {
								hoursDisplay = `${shiftHours.totalHours.toFixed(1)}h`;
							}
						}

						// For actual shifts, surface the punched start/end (BE returns them as "HH:mm").
						// Otherwise fall back to the scheduled NurseInShift times.

						const start = isActual && shiftHours ? DateTime.fromFormat(shiftHours.shiftStart, "HH:mm").toFormat("h:mma") : shift.startTime.toFormat("h:mma");
						const end = isActual && shiftHours ? DateTime.fromFormat(shiftHours.shiftEnd, "HH:mm").toFormat("h:mma") : shift.endTime.toFormat("h:mma");
						const displayText = hoursDisplay ? `${shift.name} · ${hoursDisplay}` : `${shift.name} · ${start}-${end}`;
						const baseTooltip = hoursDisplay ? `${shift.name} · ${hoursDisplay} (${start}-${end})` : `${shift.name} · ${start}-${end}`;
						const tooltipContent = isActual ? (
							<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
								<span>{baseTooltip}</span>
								<Chip label={t("actual")} size="small" sx={{ height: "16px", fontSize: "10px", fontWeight: 600, bgcolor: "#E8F4FF", color: "#1A6FB8" }} />
							</Box>
						) : (
							baseTooltip
						);

						const isSearchMatch = !!searchText && shift.name === searchText;
						return (
							<Box
								key={shift.id}
								data-testid={`high-cost-shifts-item-${shift.id}`}
								data-nurse-id={shift.nurseId}
								className={isSearchMatch ? NURSE_HIGHLIGHT_CLASS : undefined}
								onClick={onShiftDoubleClick ? scheduleHint : undefined}
								onDoubleClick={
									onShiftDoubleClick
										? () => {
												cancelHint();
												onShiftDoubleClick(shift);
											}
										: undefined
								}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									cursor: onShiftDoubleClick ? "pointer" : "default",
									"&:hover": { bgcolor: HOVER_BG },
									[`&.${NURSE_HIGHLIGHT_CLASS}`]: { bgcolor: NURSE_HIGHLIGHT_BG },
									transition: "background-color 0.2s ease",
									borderRadius: "4px",
									py: "2px",
								}}
							>
								<Divider />
								<InHouseTooltip title={tooltipContent} placement="top">
									<Typography
										sx={{
											fontWeight: 400,
											fontSize: ROW_FONT_SIZE,
											color: ROW_TEXT_COLOR,
											flex: 1,
											minWidth: 0,
											whiteSpace: "nowrap",
											overflow: "hidden",
											textOverflow: "ellipsis",
											userSelect: "none",
										}}
									>
										{displayText}
									</Typography>
								</InHouseTooltip>
								<Chip
									label={isAgency ? t("agency") : t("ot")}
									size="small"
									sx={{
										height: "18px",
										fontSize: "11px",
										fontWeight: 600,
										bgcolor: isAgency ? STATUS_BADGE.WARN_BG : STATUS_BADGE.ERROR_BG,
										color: isAgency ? STATUS_BADGE.WARN_INK : STATUS_BADGE.ERROR_INK,
									}}
								/>
							</Box>
						);
					})}
				</Box>
			</Collapse>
			<Divider />
		</Box>
	);
};
